<svelte:options

    customElement={{
        shadow: "open",
        tag: "ofc-pricing-item-v1",
        props: {
            retailPrice: { type: "Number", attribute: "retail-price" },
            benefitPrice: { type: "Number", attribute: "benefit-price"},
            benefitUnitPrice: { type: "Number", attribute: "benefit-unit-price"},
            dealHighlight: { type: "String", attribute: "deal-highlight" },
            dealId: { type: "String", attribute: "deal-id" },
            displayAlignment: { type: "String", attribute: "display-alignment", reflect: true },
            displaySize: { type: "String", attribute: "display-size", reflect: true },
            isStartingPrice: { type: "Boolean", attribute: "is-starting-price" },
            metaOriginFeature: { type: "String", attribute: "meta-origin-feature" },
            metaTeam: { type: "String", attribute: "meta-team" },
            oldPrice: { type: "Number", attribute: "old-price" },
            suggestedRetailPrice: { type: "Number", attribute: "suggested-retail-price" },
            unitPrice: { type: "Number", attribute: "unit-price" },
            unitPriceAmount: { type: "Number", attribute: "unit-price-amount" },
            unitPriceUnit: { type: "String", attribute: "unit-price-unit" },
        },
    }}
/>

<script lang="ts">
    import {
        buildAriaLabel,
        formatPrice,
        formatSavingsPercentage,
        formatValueWithUnit,
        getDeal,
        getStrikethroughPrice,
        getUnitPrice,
    } from './PricingItemV1.logic.js';
    import type { PricingItemV1Props } from './index.js';
    import { DisplaySize } from './index.js';
    import { PRICING_ITEM_V1_TAG } from './PricingItemV1.constants.js';

    const {
        retailPrice,
        benefitPrice,
        benefitUnitPrice,
        dealHighlight,
        dealId,
        displayAlignment, /*                                                                                                              */
        displaySize,
        isStartingPrice,
        oldPrice,
        suggestedRetailPrice,
        unitPrice: rawUnitPrice,
        unitPriceAmount: rawUnitPriceAmount,
        unitPriceUnit: rawUnitPriceUnit,
    }: PricingItemV1Props = $props();

    /*                                                                                        */
    /*                                                                                          */
    /*                                                                                         */
    /*                                                                                       */
    /*                                                  */
    //
    /*                                                                                         */
    /*                                                                                             */
    /*                                                                                          */
    /*                                                                    */
    let hasBenefitTag = $state(false);

    /*                                                                                      */
    /*                                                                                             */
    /*                               */
    function benefitTagSlot(wrapper: HTMLElement): { destroy(): void } {
        const slot = document.createElement('slot');
        slot.name = 'benefit-tag';
        const update = (): void => {
            hasBenefitTag = slot.assignedElements().length > 0;
        };
        slot.addEventListener('slotchange', update);
        wrapper.appendChild(slot);
        update();
        return {
            destroy(): void {
                slot.removeEventListener('slotchange', update);
                slot.remove();
                hasBenefitTag = false;
            },
        };
    }

    function warnMissingRetailPrice(): null {
        const originalValue = $host().getAttribute("retail-price");
        console.warn(`[${PRICING_ITEM_V1_TAG}] retail-price is missing or not a finite number:`, retailPrice, ", original attribute value:", originalValue, ", element:", $host());
        return null;
    }

    $effect(() => {
        const host = $host();

        /*                                                                                            */
        otto.components.sheetV1.initDataListeners(host.shadowRoot)
    })

    /*                                                         */
    /*                                                              */
    /*                                                                                   */
    /*                                                                         */
    /*                                                                                                                               */

    const strikethrough = $derived(
        getStrikethroughPrice({
            retailPrice,
            benefitPrice,
            oldPrice,
            suggestedRetailPrice,
        }),
    )

    const deal = $derived(
        getDeal({
            dealHighlight,
            dealId,
        }),
    )

    const unitPrice = $derived(
        getUnitPrice({
            unitPrice: rawUnitPrice,
            unitPriceAmount: rawUnitPriceAmount,
            unitPriceUnit: rawUnitPriceUnit,
        }),
    )

    const displayedSellingPrice = $derived(benefitPrice ?? retailPrice)

    const displayedUnitPrice = $derived(unitPrice && {...unitPrice, price: benefitUnitPrice ?? unitPrice.price});

    const saleTagSize = $derived(displaySize === DisplaySize['200'] ? "50" : "25")

    const ariaLabel = $derived(
        buildAriaLabel({
            retailPrice: displayedSellingPrice,
            isStartingPrice,
            unitPrice: displayedUnitPrice,
            strikethrough,
            deal,
        }),
    )
</script>

{#if retailPrice}
    <span class="ofc-pricing-item-v1-offscreen">{ariaLabel}</span>

    {#if deal}
        <div
            aria-hidden="true"
            class="ofc-pricing-item-v1-deal"
            data-deal-id={deal.id}
            data-testid="ofc-pricing-item-v1-deal"
        >
            {deal.highlight}
        </div>
    {/if}

    <div
        class="ofc-pricing-item-v1-retail-price"
        class:ofc-pricing-item-v1-retail-price--discounted={Boolean(strikethrough || benefitPrice)}
        data-testid="ofc-pricing-item-v1-retail-price"
        aria-hidden="true"
    >
        {#if isStartingPrice}<span class="ofc-pricing-item-v1-from-label">ab</span>&nbsp;{/if}{formatPrice(displayedSellingPrice)}
    </div>

    {#if strikethrough}
        <div aria-hidden="true" class="ofc-pricing-item-v1-strikethrough-price-container">
            {#if strikethrough.showSaleTag}
                <oc-tag-v1 size={saleTagSize} variant="sale">{formatSavingsPercentage(strikethrough.savingsPercentage)}</oc-tag-v1>
            {/if}
            <span class="ofc-pricing-item-v1-strikethrough-price">
                {#if strikethrough.isSrp}UVP&nbsp;{/if}<del>{formatPrice(strikethrough.price)}</del>
            </span>
        </div>
    {/if}

    <div
        part="benefit-tag"
        class="ofc-pricing-item-v1-benefit-tag"
        class:ofc-pricing-item-v1-benefit-tag--empty={!hasBenefitTag}
        use:benefitTagSlot
    ></div>

    {#if displayedUnitPrice}
        <div class="ofc-pricing-item-v1-unit-price" aria-hidden="true">
            {formatPrice(displayedUnitPrice.price)} / {formatValueWithUnit(displayedUnitPrice.amount.toString(), displayedUnitPrice.unit)}
        </div>
    {/if}
{:else}
    {warnMissingRetailPrice()}
{/if}

<style lang="scss">
    @use "./PricingItemV1.scss";
</style>
